import { useEffect, useMemo, useState } from "react";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  CardMedia,
  Box,
  Skeleton,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import Heading from "../../../component/Heading/Heading";
import { getFinanceRecommendations as fetchFinanceRecommendations } from "../../../services/customer-services/financeRecommendationService";
import "../../../styles/pages/customer/financeRecommendations.scss";
import CommonButton from "../../../component/button/Button";

const PAGE_STATUS = {
  LOADING: "loading",
  SUCCESS: "success",
  FORBIDDEN: "forbidden",
  ERROR: "error",
  EMPTY: "empty",
};

const FinanceRecommendationsPage = () => {
  const { t } = useTranslation();
  const [recommendations, setRecommendations] = useState([]);
  const [status, setStatus] = useState(PAGE_STATUS.LOADING);

  const getRecommendations = async () => {
    setStatus(PAGE_STATUS.LOADING);

    try {
      const response = await fetchFinanceRecommendations();
      if (response?.isSuccess) {
        const items = Array.isArray(response.data) ? response.data : [];
        setRecommendations(items);
        setStatus(items.length ? PAGE_STATUS.SUCCESS : PAGE_STATUS.EMPTY);
        return;
      }

      setRecommendations([]);
      setStatus(PAGE_STATUS.ERROR);
    } catch (error) {
      if (error?.response?.status === 403) {
        setStatus(PAGE_STATUS.FORBIDDEN);
        return;
      }

      setStatus(PAGE_STATUS.ERROR);
    }
  };

  useEffect(() => {
    getRecommendations();
  }, []);

  const statusContent = useMemo(() => {
    if (status === PAGE_STATUS.LOADING) {
      return (
        <Grid container spacing={3}>
          {[1, 2, 3, 4].map((item) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={item}>
              <Card variant="outlined" className="finance-recommendations-skeleton-card">
                <Skeleton variant="rectangular" height={140} />
                <CardContent>
                  <Skeleton variant="text" height={32} width="70%" />
                  <Skeleton variant="text" />
                  <Skeleton variant="text" width="80%" />
                  <Skeleton variant="rounded" height={36} width={150} />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      );
    }

    if (status === PAGE_STATUS.FORBIDDEN) {
      return (
        <Box className="finance-recommendations-state">
          <Typography variant="h6">
            {t("finance_recommendations_admin_forbidden", {
              defaultValue:
                "Financial solutions are currently not available for your account.",
            })}
          </Typography>
          <Typography variant="body2">
            {t("finance_recommendations_admin_forbidden_hint", {
              defaultValue:
                "Please contact support if you need access to this section.",
            })}
          </Typography>
        </Box>
      );
    }

    if (status === PAGE_STATUS.ERROR) {
      return (
        <Box className="finance-recommendations-state">
          <Typography variant="h6">
            {t("finance_recommendations_error_title", {
              defaultValue: "Could not load financing options",
            })}
          </Typography>
          <Typography variant="body2">
            {t("finance_recommendations_error_hint", {
              defaultValue:
                "Please try again. If the issue continues, contact support.",
            })}
          </Typography>
          <CommonButton
            type="button"
            onClick={getRecommendations}
            text={t("retry", { defaultValue: "Retry" })}
            className="add-user-btn button-border-radius"
          />
        </Box>
      );
    }

    if (status === PAGE_STATUS.EMPTY) {
      return (
        <Box className="finance-recommendations-state">
          <Typography variant="h6">
            {t("finance_recommendations_empty_title", {
              defaultValue: "No financing options available right now",
            })}
          </Typography>
          <Typography variant="body2">
            {t("finance_recommendations_empty_hint", {
              defaultValue: "Please check back later.",
            })}
          </Typography>
        </Box>
      );
    }

    return (
      <Grid container spacing={3}>
        {recommendations.map((bank) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={bank._id}>
            <Card
              variant="outlined"
              sx={{
                borderRadius: 2,
                height: "100%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
              }}
            >
              <CardMedia
                component="img"
                image={bank.logoUrl}
                alt={bank.name}
                sx={{
                  width: "100%",
                  height: 140,
                  objectFit: "contain",
                  padding: 2,
                  backgroundColor: "#f9f9f9",
                }}
              />

              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {bank.name}
                </Typography>

                {bank.description.split("\n").map((line, i) => (
                  <Typography key={i} variant="body2" sx={{ mb: 0.5 }}>
                    {line.replace(/^- /, "• ")}
                  </Typography>
                ))}
              </CardContent>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  padding: 2,
                }}
              >
                <CommonButton
                  type="button"
                  onClick={() =>
                    window.open(bank.refLink, "_blank", "noopener,noreferrer")
                  }
                  text={t("go_to_bank")}
                  className="add-user-btn button-border-radius"
                />
              </Box>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  }, [recommendations, status, t]);

  return (
    <div className="finance-recommendations-wrapper">
      <Box className="finance-recommendations-heading" mb={3}>
        <Heading text={t("module_financerecommendation")} />
      </Box>

      {statusContent}
    </div>
  );
};

export default FinanceRecommendationsPage;
